import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { NodeViewWrapper, useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import { generateTranscript, enhanceTranscript, getAudioFileFromUrl } from '../utils/transcriptService';
import { ActiveEditorContext } from '../context/ActiveEditorContext';
import './ResizableAudio.css';

const BAR_COUNT = 56;

const PlayIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
);
const PauseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="5" width="4" height="14" rx="1" />
    <rect x="14" y="5" width="4" height="14" rx="1" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
    <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0" />
  </svg>
);
const TranscriptIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
  </svg>
);
const EyeIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ResizableAudio = ({ node, updateAttributes, onDelete, editor, getPos }) => {
  const isStudent = (() => {
    try {
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-student-mode');
      if (fromRoot) return fromRoot === 'true';
      return typeof document !== 'undefined' &&
        (window.location.pathname.includes('/student-dashboard') ||
         window.location.pathname.includes('/student-profile'));
    } catch (_) { return false; }
  })();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);
  const [isGeneratingTranscript, setIsGeneratingTranscript] = useState(false);
  const [transcriptError, setTranscriptError] = useState(null);
  const [isEditingTranscript, setIsEditingTranscript] = useState(false);
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const { setActiveEditor } = useContext(ActiveEditorContext);

  const src = node.attrs.src;

  // Decorative bar heights — purely cosmetic (this isn't a real amplitude
  // waveform), but they must stay stable across re-renders. Recomputing
  // with Math.random() directly in the render (the old behavior) made every
  // bar jitter to a new height on every timeupdate tick while playing.
  const barHeights = useMemo(
    () => Array.from({ length: BAR_COUNT }, () => Math.random() * 15 + 5),
    []
  );

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      window.dispatchEvent(new CustomEvent('docMediaPublish', { detail: { src, action: 'play',  currentTime: audio.currentTime } }));
    };

    const handlePause = () => {
      setIsPlaying(false);
      window.dispatchEvent(new CustomEvent('docMediaPublish', { detail: { src, action: 'pause', currentTime: audio.currentTime } }));
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    const handleSync = (e) => {
      const { src: eSrc, action, currentTime } = e.detail || {};
      if (eSrc !== src) return;
      if (typeof currentTime === 'number') audio.currentTime = currentTime;
      if (action === 'play')  { audio.play().catch(() => {}); }
      else if (action === 'pause') { audio.pause(); }
    };
    window.addEventListener('docMediaSync', handleSync);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      window.removeEventListener('docMediaSync', handleSync);
    };
  }, [src]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleSeek = (e) => {
    if (audioRef.current && duration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percent = Math.max(0, Math.min(1, clickX / rect.width));
      const newTime = percent * duration;
      audioRef.current.currentTime = Math.min(newTime, duration);
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
      return;
    }
    if (editor && typeof getPos === 'function') {
      const pos = getPos();
      if (typeof pos === 'number') {
        editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
      }
    }
  };

  const handleEditTranscript = () => {
    setIsEditingTranscript(true);
    if (transcriptEditor) {
      transcriptEditor.commands.setContent(node.attrs.transcript || '');
    }
  };

  const handleSaveTranscript = () => {
    if (updateAttributes && transcriptEditor) {
      updateAttributes({ transcript: transcriptEditor.getHTML() });
    }
    setIsEditingTranscript(false);
  };

  const handleCancelEdit = () => {
    setIsEditingTranscript(false);
  };

  // TipTap editor for the transcript. `useEditor` itself always runs (React
  // hooks can't be conditional), but its output is only ever rendered via
  // <EditorContent> below while isEditingTranscript is true — the read-only
  // display further down is a plain div, not this editor. This matches the
  // original, working version of this component: no wrapping div around
  // EditorContent, sizing set via its own inline style. (An attempt to keep
  // this editor permanently mounted and just toggle its `editable` state,
  // to avoid the DOM swap, made the width bug show up even in read-only
  // view — proof the bug is with this nested editor's presence at all, not
  // with switching modes, so that approach was reverted.)
  const transcriptEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        horizontalRule: false,
        dropcursor: { color: '#dee2e6', width: 1 },
      }),
      Bold,
      Italic,
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({ types: ['paragraph'] }),
    ],
    content: node.attrs.transcript || '',
    editorProps: {
      attributes: {
        class: 'ra-transcript-editor',
      },
    },
    onFocus: ({ editor }) => {
      setActiveEditor(editor);
    },
  });

  const toggleTranscript = async () => {
    // Students can only view a transcript the teacher has already shared —
    // never generate one themselves. This button isn't even rendered for a
    // student unless transcriptSharedWithStudent is set (see below), but
    // guard here too in case that ever changes.
    if (isStudent) {
      setShowTranscript(!showTranscript);
      return;
    }
    // If no transcript exists, generate one first
    if (!node.attrs.transcript && !isGeneratingTranscript) {
      await generateTranscriptFromAudio();
    } else {
      setShowTranscript(!showTranscript);
      if (!showTranscript) {
        setIsEditingTranscript(false);
      }
    }
  };

  const handleToggleShareWithStudent = () => {
    if (updateAttributes) {
      updateAttributes({ transcriptSharedWithStudent: !node.attrs.transcriptSharedWithStudent });
    }
  };

  const generateTranscriptFromAudio = async () => {
    if (!src) {
      setTranscriptError('No audio source available');
      return;
    }

    setIsGeneratingTranscript(true);
    setTranscriptError(null);

    try {
      const audioFile = await getAudioFileFromUrl(src);
      const rawTranscript = await generateTranscript(audioFile);
      const enhancedTranscript = await enhanceTranscript(rawTranscript);

      // Additional cleanup to remove any remaining formatting
      const cleanTranscript = enhancedTranscript
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove **bold**
        .replace(/\*(.*?)\*/g, '$1')     // Remove *italic*
        .replace(/__(.*?)__/g, '$1')    // Remove __bold__
        .replace(/_(.*?)_/g, '$1')      // Remove _italic_
        .replace(/`(.*?)`/g, '$1')      // Remove `code`
        .replace(/#{1,6}\s*/g, '')      // Remove headers
        .replace(/\n\s*\n/g, '\n')      // Remove extra line breaks
        .trim();

      if (updateAttributes) {
        updateAttributes({ transcript: cleanTranscript });
      }

      setShowTranscript(true);
    } catch (error) {
      console.error('Failed to generate transcript:', error);
      setTranscriptError(error.message || 'Failed to generate transcript');
    } finally {
      setIsGeneratingTranscript(false);
    }
  };

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (containerRef.current) {
      const editor = containerRef.current.closest('.ProseMirror')?.__editor;
      if (editor) {
        const pos = editor.view.posAtDOM(containerRef.current, 0);
        editor.commands.setTextSelection(pos);
        editor.commands.focus();
      }
    }
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (containerRef.current) {
      const editor = containerRef.current.closest('.ProseMirror')?.__editor;
      if (editor) {
        const pos = editor.view.posAtDOM(containerRef.current, 1);
        editor.commands.setTextSelection(pos);
        editor.commands.focus();
      }
    }
  };

  // Drag handlers — same recipe as the exercise embeds. Plain `draggable`
  // alone (what the video node uses) wasn't enough here: this component is
  // covered edge-to-edge in interactive children (play button, seek track,
  // transcript/delete buttons), leaving ProseMirror no unambiguous "empty"
  // area to recognize as the drag origin. `data-drag-handle` tells it to
  // treat the whole wrapper as the handle regardless of what's inside.
  const handleDragStart = (e) => {
    try {
      e.target.style.opacity = '0.5';
      e.target.classList.add('dragging');
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'copyMove';
        const payload = { type: 'audioNode', attrs: { src, width: node.attrs.width, height: node.attrs.height, transcript: node.attrs.transcript } };
        try { e.dataTransfer.setData('application/x-novas-node', JSON.stringify(payload)); } catch (_) {}
      }
    } catch (_) {}
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
    e.target.classList.remove('dragging');
  };

  const progress = duration > 0 ? (currentTime / duration) * BAR_COUNT : 0;
  const transcriptShown = isStudent
    ? !!(node.attrs.transcriptSharedWithStudent && node.attrs.transcript)
    : true;

  return (
    <NodeViewWrapper
      className="ra-embed"
      data-type="audio"
      draggable
      data-drag-handle=""
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove} />
      <div className="ra-card" ref={containerRef}>
        <div className="ra-actions">
          {!isStudent && (
            <button className="ra-icon-btn is-delete" onClick={handleDelete} data-tip="Delete audio">
              <TrashIcon />
            </button>
          )}

          {transcriptShown && (
            <button
              className={`ra-icon-btn is-transcript${isGeneratingTranscript ? ' is-generating' : ''}`}
              onClick={toggleTranscript}
              disabled={isGeneratingTranscript}
              data-tip={isStudent
                ? 'Show transcript'
                : isGeneratingTranscript
                  ? 'Generating transcript...'
                  : node.attrs.transcript
                    ? 'Show transcript'
                    : 'Generate transcript'
              }
            >
              {isGeneratingTranscript ? <span className="ra-spinner" /> : <TranscriptIcon />}
            </button>
          )}
        </div>

        <audio
          ref={audioRef}
          src={src}
          controls={false}
          draggable={false}
          className="ra-native-audio"
        />

        <div className="ra-player">
          <div className="ra-player-row">
            <button className={`ra-play-btn${isPlaying ? ' is-playing' : ''}`} onClick={togglePlay}>
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>

            <div className="ra-track-wrap">
              <div className="ra-track" onClick={handleSeek} data-seek="true">
                {barHeights.map((h, i) => {
                  const isPlayed = i < progress;
                  const isCurrent = Math.floor(progress) === i;
                  return (
                    <div
                      key={i}
                      className={`ra-bar${isPlayed ? ' is-played' : ''}${isCurrent ? ' is-current' : ''}${isPlaying && isPlayed ? ' is-playing-anim' : ''}`}
                      style={{ height: `${h}px` }}
                    />
                  );
                })}
              </div>
              <div className="ra-time-row">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
          </div>
        </div>

        {showTranscript && transcriptShown && (
          <div className="ra-transcript">
            <div className="ra-transcript-head">
              <div className="ra-transcript-title">
                <strong>Transcript</strong>
                {node.attrs.transcript && (
                  <span className="ra-transcript-hint">
                    {isStudent ? '(select text to translate)' : '(Double-click to edit, select text to translate)'}
                  </span>
                )}
              </div>
              {!isStudent && !transcriptError && node.attrs.transcript && (
                <div className="ra-transcript-actions">
                  <button
                    className={`ra-pill-btn is-share${node.attrs.transcriptSharedWithStudent ? ' is-active' : ''}`}
                    onClick={handleToggleShareWithStudent}
                    title={node.attrs.transcriptSharedWithStudent
                      ? 'Visible to student — click to hide'
                      : 'Not visible to student yet — click to share'
                    }
                  >
                    <EyeIcon />
                    {node.attrs.transcriptSharedWithStudent ? 'Shared with student' : 'Share with student'}
                  </button>
                  <button className="ra-pill-btn is-edit" onClick={handleEditTranscript}>
                    Edit
                  </button>
                </div>
              )}
            </div>

            {transcriptError ? (
              <div className="ra-transcript-error">
                <strong>Error:</strong> {transcriptError}
                <br />
                <small>Please check your OpenAI API key configuration.</small>
              </div>
            ) : isEditingTranscript ? (
              <div>
                {transcriptEditor && (
                  // No wrapping div here on purpose — the old, working version
                  // of this component rendered EditorContent directly with its
                  // sizing set via an inline style, with nothing else around
                  // it. An extra wrapper with overflow:hidden (which is what
                  // broke it) was added later during the visual redesign.
                  <EditorContent
                    editor={transcriptEditor}
                    style={{
                      width: '100%',
                      minHeight: '120px',
                      padding: '10px 12px',
                      border: '1px solid rgba(16, 24, 40, 0.1)',
                      borderRadius: '10px',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      background: '#fff',
                    }}
                  />
                )}
                <div className="ra-transcript-edit-actions">
                  <button className="ra-pill-btn is-save" onClick={handleSaveTranscript}>Save</button>
                  <button className="ra-pill-btn is-cancel" onClick={handleCancelEdit}>Cancel</button>
                </div>
              </div>
            ) : (
              <div
                className="ra-transcript-body"
                dangerouslySetInnerHTML={{
                  __html: node.attrs.transcript || 'Transcript is not available for this audio file. Click the transcript button to generate one with AI.'
                }}
                onDoubleClick={() => {
                  if (!isStudent && node.attrs.transcript) {
                    handleEditTranscript();
                  }
                }}
              />
            )}
          </div>
        )}
      </div>
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow} />
    </NodeViewWrapper>
  );
};

export default ResizableAudio;
